import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload, faEye, faFileContract, faFileInvoice, faShip, faSpinner,
} from '@fortawesome/free-solid-svg-icons';

import axiosInstance from '../utils/axiosConfig';
import PdfPreviewModal from './shared/PdfPreviewModal';
import { useDocumentViewer } from './DocumentsSplitPane';
import { DELIVERY_DOCUMENT_CATEGORIES } from '../constants/documentTypeCategories';
import { arrivesWithInvoicePapers } from '../utils/invoiceScreenPath';
import './Legal/ContractsInContextPanel.css';
import './RelatedDocumentsPane.css';

/**
 * The papers an approver checks an invoice against, beside the invoice.
 *
 * ClickUp, 2026-10-23: "Please add the double screen functionality everywhere
 * there are approvals e.g.: finance checking invoices against deliveries,
 * contracts against invoices, bunker notes / BL against invoices". The double
 * screen is the delivery's Contracts & Notes split, so this is the body of a
 * ``DocumentsSplitPane`` on the incoming and outgoing invoice screens, and
 * since phase 2 on the pro-forma, the purchase order and the credit and debit
 * notes (a note also shows the invoice it corrects).
 *
 * READ-ONLY by construction: there is no upload, no rename and no delete here,
 * and the server doors behind it are GET only. Finance holds no delivery
 * access, so every paper comes through the INVOICE's own doors
 * (``/api/invoicing/<kind>/<id>/related-documents/``), never the delivery's.
 *
 * Inside the pane a paper opens BESIDE the invoice (``useDocumentViewer``);
 * on a screen with no pane it falls back to the pop-up, like every other
 * panel that shares the viewer.
 *
 * An approvals list (bunker invoice approvals, the credit approval screen)
 * opens an invoice WITH the pane already showing: its link carries
 * ``papers=1`` (``utils/invoiceScreenPath``), and ``useInvoicePapersOpen``
 * reads that flag, so the approver never has to find the button.
 */

// The two papers an approver checks first. Must stay in step with BDN_LABELS /
// BL_LABELS in backend/invoicing/views_invoice_related_documents.py.
const KEY_PAPER_TYPES = [
    'Bunker Delivery Note (BDN)',
    'Bill of Lading (BL)',
    'Non-Negotiable Bills of Lading',
];

export const INVOICE_PAPERS_STORAGE_KEY = 'imt.invoice-papers-open';

const readRemembered = (storageKey) => {
    try {
        return window.localStorage.getItem(storageKey) === '1';
    } catch (err) {
        return false;
    }
};

/**
 * Whether a papers pane is open, remembered per browser under ``storageKey``.
 * Closed by default, unless ``startOpen`` says the screen was opened with the
 * papers asked for (then the pane shows, and is remembered open from here).
 * Storage can be blocked (private window), so every read and write is
 * guarded and the screen works the same without it.
 */
export const useRememberedOpen = (storageKey, startOpen = false) => {
    const [isOpen, setIsOpen] = useState(() => startOpen || readRemembered(storageKey));

    useEffect(() => {
        try {
            window.localStorage.setItem(storageKey, isOpen ? '1' : '0');
        } catch (err) {
            // Not remembered on this browser; the pane still opens and closes.
        }
    }, [storageKey, isOpen]);

    const toggle = useCallback(() => setIsOpen((open) => !open), []);
    const close = useCallback(() => setIsOpen(false), []);
    return { isOpen, toggle, close };
};

/**
 * Whether the invoice papers pane is open, remembered per browser.
 *
 * Shared by every screen with the Invoice Papers button (the invoices, the
 * pro-forma, the purchase order and the notes) so an approver who
 * works with the papers open finds them open on the next document. A screen
 * opened from an approvals list arrives with ``papers=1`` in its address and
 * starts open whatever the browser remembered.
 */
export const useInvoicePapersOpen = () => {
    const { search } = useLocation();
    return useRememberedOpen(INVOICE_PAPERS_STORAGE_KEY, arrivesWithInvoicePapers(search));
};

export const isPdfName = (...names) => names.some(
    (name) => typeof name === 'string' && /\.pdf$/i.test(name.trim())
);

// DocuWare answers dates as ISO strings or as .NET JSON dates (/Date(ms)/).
export const formatPaperDate = (value) => {
    if (!value) return '';
    const net = typeof value === 'string' ? value.match(/\/Date\((\d+)\)\//) : null;
    const date = net ? new Date(Number(net[1])) : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatSize = (bytes) => {
    const n = Number(bytes);
    if (!n || n < 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** Key papers first, then the delivery categories, then everything else. */
export const groupDeliveryPapers = (papers) => {
    const keyPapers = papers.filter((p) => KEY_PAPER_TYPES.includes(p.document_type));
    const rest = papers.filter((p) => !KEY_PAPER_TYPES.includes(p.document_type));
    const groups = [];
    if (keyPapers.length) {
        // BDN before BL, whatever order the list came in.
        const rank = (p) => KEY_PAPER_TYPES.indexOf(p.document_type);
        groups.push({
            id: 'key',
            label: 'BDN and Bill of Lading',
            papers: [...keyPapers].sort((a, b) => rank(a) - rank(b)),
        });
    }
    const placed = new Set();
    DELIVERY_DOCUMENT_CATEGORIES.forEach((category) => {
        const inCategory = rest.filter((p) => category.types.includes(p.document_type));
        inCategory.forEach((p) => placed.add(p));
        if (inCategory.length) groups.push({ id: category.id, label: category.label, papers: inCategory });
    });
    const other = rest.filter((p) => !placed.has(p));
    if (other.length) groups.push({ id: 'other', label: 'Other', papers: other });
    return groups;
};

// The sentence the server sent with a refused or failed file (``detail`` /
// ``error``), read out of the blob the file request asked for. '' when there
// is none, and the caller falls back to "Try again".
const blobText = (blob) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => resolve('');
    reader.readAsText(blob);
});

const serverSentence = async (err) => {
    const data = err && err.response && err.response.data;
    if (!data) return '';
    try {
        const text = typeof Blob !== 'undefined' && data instanceof Blob
            ? await blobText(data)
            : (typeof data === 'string' ? data : JSON.stringify(data));
        const parsed = JSON.parse(text);
        const sentence = parsed && (parsed.detail || parsed.error);
        // Only a plain sentence: a nested error would print as "[object Object]".
        return typeof sentence === 'string' ? sentence : '';
    } catch (e) {
        return '';
    }
};

/**
 * View and Download for one paper, shared by every papers pane (the invoice
 * papers here and the KYC case papers). View reads the paper BESIDE the record
 * when the pane offers a viewer, else in the pop-up (``previewModal``, which
 * the pane renders once). Every blob URL made is released when the pane goes
 * away; the viewer may still be showing one, so they are not revoked earlier.
 */
export const usePaperActions = () => {
    const viewer = useDocumentViewer();
    const [busyKey, setBusyKey] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [preview, setPreview] = useState(null);
    const blobUrls = useRef([]);

    useEffect(() => () => {
        blobUrls.current.forEach((url) => window.URL.revokeObjectURL(url));
        blobUrls.current = [];
    }, []);

    const fetchFile = async (url, type) => {
        const response = await axiosInstance.get(url, { responseType: 'blob' });
        const blob = type
            ? new Blob([response.data], { type })
            : new Blob([response.data]);
        const objectUrl = window.URL.createObjectURL(blob);
        blobUrls.current.push(objectUrl);
        return objectUrl;
    };

    const openPaper = async (key, url, name) => {
        setBusyKey(`view-${key}`);
        setActionError(null);
        try {
            const objectUrl = await fetchFile(url, 'application/pdf');
            if (viewer) {
                viewer.open(objectUrl, name);
            } else {
                setPreview({ url: objectUrl, name });
            }
        } catch (err) {
            const why = await serverSentence(err);
            setActionError(why ? `Could not open "${name}". ${why}` : `Could not open "${name}". Try again.`);
        } finally {
            setBusyKey(null);
        }
    };

    const downloadPaper = async (key, url, name) => {
        setBusyKey(`download-${key}`);
        setActionError(null);
        try {
            const objectUrl = await fetchFile(url);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = name || 'document.pdf';
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            const why = await serverSentence(err);
            setActionError(why ? `Could not download "${name}". ${why}` : `Could not download "${name}". Try again.`);
        } finally {
            setBusyKey(null);
        }
    };

    const actions = ({ key, url, name, canView }) => (
        <div className="rdp-actions">
            {canView && (
                <button
                    type="button"
                    className="rdp-btn"
                    onClick={() => openPaper(key, url, name)}
                    disabled={busyKey === `view-${key}`}
                    aria-label={`View ${name}`}
                >
                    <FontAwesomeIcon
                        icon={busyKey === `view-${key}` ? faSpinner : faEye}
                        spin={busyKey === `view-${key}`}
                    />
                    View
                </button>
            )}
            <button
                type="button"
                className="rdp-btn"
                onClick={() => downloadPaper(key, url, name)}
                disabled={busyKey === `download-${key}`}
                aria-label={`Download ${name}`}
            >
                <FontAwesomeIcon
                    icon={busyKey === `download-${key}` ? faSpinner : faDownload}
                    spin={busyKey === `download-${key}`}
                />
                Download
            </button>
        </div>
    );

    // Only for a host with no pane: inside the pane the paper opens beside
    // the record instead.
    const previewModal = (
        <PdfPreviewModal
            isOpen={Boolean(preview) && !viewer}
            pdfUrl={preview ? preview.url : null}
            fileName={preview ? preview.name : ''}
            onClose={() => setPreview(null)}
        />
    );

    return { actionError, actions, previewModal };
};

// What the pane calls the record it is anchored on, per kind.
const KIND_WORDS = {
    incoming: { title: 'This Invoice', noun: 'invoice' },
    outgoing: { title: 'This Invoice', noun: 'invoice' },
    proforma: { title: 'This Pro-forma', noun: 'pro-forma' },
    'purchase-order': { title: 'This Purchase Order', noun: 'purchase order' },
    'credit-note': { title: 'This Note', noun: 'note' },
    'debit-note': { title: 'This Note', noun: 'note' },
};

export const INVOICE_PAPER_KINDS = Object.keys(KIND_WORDS);

const RelatedDocumentsPane = ({ kind, invoiceId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const { actionError, actions, previewModal } = usePaperActions();
    const words = KIND_WORDS[kind] || KIND_WORDS.incoming;

    const load = useCallback(async () => {
        if (!invoiceId) return;
        setLoading(true);
        setLoadFailed(false);
        try {
            const response = await axiosInstance.get(
                `/api/invoicing/${kind}/${invoiceId}/related-documents/`
            );
            setData(response.data || null);
        } catch (err) {
            // "We could not ask" is never shown as "there is nothing".
            setData(null);
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [kind, invoiceId]);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div className="rdp" aria-busy="true">
                <p className="rdp-muted rdp-loading">
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading the invoice papers…
                </p>
            </div>
        );
    }

    if (loadFailed || !data) {
        return (
            <div className="rdp">
                <div className="rdp-problem" role="alert">
                    <p className="rdp-problem-text">Could not load the invoice papers. Try again.</p>
                    <button type="button" className="rdp-btn" onClick={load}>Try Again</button>
                </div>
            </div>
        );
    }

    const invoice = data.invoice || {};
    const ownDocuments = invoice.documents || [];
    const delivery = data.delivery;
    const papers = data.delivery_documents || [];
    const contracts = data.contracts || [];
    const groups = groupDeliveryPapers(papers);
    // A credit note's outgoing invoice, a debit note's supplier invoice.
    const original = data.original_invoice || null;

    // A record's own PDF rows (this record, or a note's original invoice).
    const pdfRows = (documents, prefix) => documents.map((doc) => (
        <div className="rdp-row" key={`${prefix}-${doc.document_id ?? doc.name}`}>
            <div className="rdp-row-main">
                <span className="rdp-name" title={doc.name}>{doc.name}</span>
                {doc.uploaded_at && (
                    <div className="rdp-meta">
                        <span>{formatPaperDate(doc.uploaded_at)}</span>
                    </div>
                )}
            </div>
            {actions({
                key: `${prefix}-${doc.document_id ?? 'pdf'}`,
                url: doc.download_url,
                name: doc.name,
                canView: Boolean(doc.is_pdf),
            })}
        </div>
    ));

    return (
        <div className="rdp">
            {actionError && (
                <p className="rdp-error" role="alert">{actionError}</p>
            )}

            <section className="rdp-section" aria-labelledby="rdp-invoice-title">
                <h3 className="rdp-section-title" id="rdp-invoice-title">
                    <FontAwesomeIcon icon={faFileInvoice} /> {words.title}
                </h3>
                {invoice.error && (
                    <p className="rdp-error">{invoice.error}</p>
                )}
                {!invoice.error && ownDocuments.length === 0 && (
                    <p className="rdp-muted">
                        {kind === 'purchase-order'
                            ? 'IMT makes no PDF for a purchase order.'
                            : `No PDF is attached to this ${words.noun} yet.`}
                    </p>
                )}
                {pdfRows(ownDocuments, 'own')}
            </section>

            {original && (
                <section className="rdp-section" aria-labelledby="rdp-original-title" data-section="original-invoice">
                    <h3 className="rdp-section-title" id="rdp-original-title">
                        <FontAwesomeIcon icon={faFileInvoice} />
                        {original.number ? `Original Invoice ${original.number}` : 'Original Invoice'}
                    </h3>
                    {original.error && (
                        <p className="rdp-error">{original.error}</p>
                    )}
                    {!original.error && (original.documents || []).length === 0 && (
                        <p className="rdp-muted">No PDF is attached to the original invoice yet.</p>
                    )}
                    {pdfRows(original.documents || [], 'original')}
                </section>
            )}

            <section className="rdp-section" aria-labelledby="rdp-delivery-title">
                <h3 className="rdp-section-title" id="rdp-delivery-title">
                    <FontAwesomeIcon icon={faShip} />
                    {delivery ? `Delivery ${delivery.number || delivery.id}` : 'Delivery'}
                </h3>
                {!delivery && (
                    <p className="rdp-muted">{`No delivery linked to this ${words.noun}.`}</p>
                )}
                {delivery && data.delivery_documents_error && (
                    <div className="rdp-problem" role="alert">
                        <p className="rdp-problem-text">Could not load the delivery papers. Try again.</p>
                        <p className="rdp-muted">{data.delivery_documents_error}</p>
                        <button type="button" className="rdp-btn" onClick={load}>Try Again</button>
                    </div>
                )}
                {delivery && !data.delivery_documents_error && papers.length === 0 && (
                    <p className="rdp-muted">No papers on this delivery yet.</p>
                )}
                {groups.map((group) => (
                    <div className="rdp-group" key={group.id} data-group={group.id}>
                        <h4 className="rdp-group-title">{group.label}</h4>
                        {group.papers.map((paper) => (
                            <div className="rdp-row" key={paper.document_id} data-paper-id={paper.document_id}>
                                <div className="rdp-row-main">
                                    <span className="rdp-name" title={paper.name}>{paper.name}</span>
                                    <div className="rdp-meta">
                                        <span>{paper.document_type || 'Other'}</span>
                                        {paper.uploaded_at && <span>{formatPaperDate(paper.uploaded_at)}</span>}
                                        {formatSize(paper.size) && <span>{formatSize(paper.size)}</span>}
                                    </div>
                                </div>
                                {paper.can_open ? actions({
                                    key: `paper-${paper.document_id}`,
                                    url: paper.download_url,
                                    name: paper.original_filename || paper.name,
                                    canView: isPdfName(paper.original_filename, paper.name),
                                }) : (
                                    <span className="rdp-muted rdp-note">Open it from the delivery screen.</span>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </section>

            {/* Same row look as the contracts panel on the delivery and the
                enquiry (ContractsInContextPanel), fed from the invoice's own
                door: a finance reader is not in Legal's own visibility rule,
                so that panel's request would come back empty for them. */}
            <section className="rdp-section contracts-in-context" aria-labelledby="rdp-contracts-title">
                <h3 className="cic-title rdp-section-title" id="rdp-contracts-title">
                    <FontAwesomeIcon icon={faFileContract} /> Contracts
                </h3>
                {delivery && contracts.length === 0 && (
                    <p className="cic-muted">No contracts linked to this delivery.</p>
                )}
                {!delivery && (
                    <p className="cic-muted">No delivery linked, so there are no contracts to show.</p>
                )}
                {contracts.map((contract) => (
                    <div key={contract.id} className="cic-row">
                        <div className="cic-row-head">
                            <span className="cic-number">{contract.contract_number}</span>
                            <span className="cic-type">{contract.contract_type_display}</span>
                            {contract.counterparty_name && (
                                <span className="cic-counterparty">{contract.counterparty_name}</span>
                            )}
                        </div>
                        <div className="cic-meta">
                            {contract.entity_name && <span>{contract.entity_name}</span>}
                            {contract.trade_recap_number && <span>{contract.trade_recap_number}</span>}
                            {contract.freight_recap_number && <span>{contract.freight_recap_number}</span>}
                            {contract.commence_date && <span>{contract.commence_date}</span>}
                        </div>
                        {(contract.files || []).length === 0 ? (
                            <p className="cic-muted cic-nofile">No file attached.</p>
                        ) : (
                            <ul className="cic-files">
                                {contract.files.map((file) => (
                                    <li key={file.id} className="rdp-file">
                                        <span className="rdp-name" title={file.original_filename}>
                                            {file.original_filename}
                                        </span>
                                        {actions({
                                            key: `contract-${file.id}`,
                                            url: file.download_url,
                                            name: file.original_filename || `${contract.contract_number}.pdf`,
                                            canView: isPdfName(file.original_filename),
                                        })}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ))}
            </section>

            {previewModal}
        </div>
    );
};

RelatedDocumentsPane.propTypes = {
    kind: PropTypes.oneOf(INVOICE_PAPER_KINDS).isRequired,
    invoiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default RelatedDocumentsPane;
